/**
 * Notifications — Benachrichtigungszentrale mit Kategorie-Filterung.
 *
 * Features:
 * - Filterung nach Kategorie (Forecast, Schicht, Abwesenheit, System, User)
 * - Nur-Ungelesen-Filter
 * - Alle-als-gelesen-markieren
 * - Paginierung und Navigation zum Kontext
 *
 * @page /notifications
 * @roles alle (authentifiziert)
 * @i18n notifications, common
 * @endpoints GET /api/notifications, PUT /api/notifications/alle-gelesen, PUT /api/notifications/:id/gelesen
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Calendar, Umbrella, Settings, User, Bell } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

function timeAgo(iso, t) {
 const diff = Date.now() - new Date(iso).getTime();
 const s = Math.floor(diff / 1000);
 if (s < 60) return t('notifications.justNow');
 const m = Math.floor(s / 60);
 if (m < 60) return t('notifications.minutesAgo', { m });
 const h = Math.floor(m / 60);
 if (h < 24) return t('notifications.hoursAgo', { h });
 const d = Math.floor(h / 24);
 if (d < 7) return t('notifications.daysAgo', { d });
 return new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const typIconMap = { forecast: BarChart3, schicht: Calendar, abwesenheit: Umbrella, system: Settings, user: User };
function getTypIcon(typ) { const Icon = typIconMap[typ?.split('.')[0]] || Bell; return <Icon className="w-4 h-4" />; }

export default function Notifications() {
 const navigate = useNavigate();
 const { t } = useLanguage();
 const [items, setItems] = useState([]);
 const [total, setTotal] = useState(0);
 const [loading, setLoading] = useState(true);
 const [offset, setOffset] = useState(0);
 const [filter, setFilter] = useState('');
 const [onlyUnread, setOnlyUnread] = useState(false);
 const limit = 20;

 const CATEGORIES = [
 { value: '', label: t('common.all') },
 { value: 'forecast', label: 'Forecast' },
 { value: 'schicht', label: t('notifications.shiftPlanning') },
 { value: 'abwesenheit', label: t('notifications.absence') },
 { value: 'system', label: 'System' },
 { value: 'user', label: 'User' },
 ];

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams({ limit, offset });
 if (onlyUnread) params.set('ungelesen', 'true');
 if (filter) params.set('kategorie', filter);
 const res = await fetch(`${API}/api/notifications?${params}`);
 if (res.ok) {
 const data = await res.json();
 setItems(data.notifications || []);
 setTotal(data.total || 0);
 }
 } catch { /* ignore */ }
 finally { setLoading(false); }
 }, [offset, filter, onlyUnread]);

 useEffect(() => { load(); }, [load]);

 const markAllRead = async () => {
 await fetch(`${API}/api/notifications/alle-gelesen`, { method: 'PUT' });
 setItems(items.map(n => ({ ...n, gelesen: true })));
 };

 const markOneRead = async (notif) => {
 if (!notif.gelesen) {
 fetch(`${API}/api/notifications/${notif.id}/gelesen`, { method: 'PUT' }).catch(() => {});
 setItems(items.map(n => n.id === notif.id ? { ...n, gelesen: true } : n));
 }
 if (notif.link) navigate(notif.link);
 };

 const totalPages = Math.ceil(total / limit);
 const currentPage = Math.floor(offset / limit) + 1;

 return (
 <div className="p-6">
 <div className="flex items-center justify-between mb-6">
 <div>
 <h1 className="text-lg font-bold text-content">{t('notifications.title')}</h1>
 <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('notifications.total', { count: total })}</p>
 </div>
 <button onClick={markAllRead} className="text-xs text-primary-400 hover:text-primary-300 font-medium">{t('notifications.markAllRead')}</button>
 </div>

 {/* Filters */}
 <div className="flex items-center gap-2 mb-4 flex-wrap">
 <div className="flex gap-1 bg-surface-card p-1 rounded-card">
 {CATEGORIES.map(c => (
 <button
 key={c.value}
 onClick={() => { setFilter(c.value); setOffset(0); }}
 className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${filter === c.value ? 'bg-white dark:bg-surface-card shadow-sm text-content font-semibold' : 'text-[var(--text-secondary)] hover:text-content-secondary'}`}
 >{c.label}</button>
 ))}
 </div>
 <button
 onClick={() => { setOnlyUnread(!onlyUnread); setOffset(0); }}
 className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-colors ${onlyUnread ? 'border-primary-500/30 bg-primary-500/10 text-primary-400' : 'border-line-hover text-[var(--text-secondary)] hover:text-content-secondary'}`}
 >{t('notifications.onlyUnread')}</button>
 </div>

 {/* List */}
 <div className="bg-surface-card border border-line-card rounded-card overflow-hidden shadow-card">
 {loading ? (
 <div className="flex justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : items.length === 0 ? (
 <div className="py-12 text-center text-xs text-[var(--text-tertiary)]">{t('notifications.noNotifications')}</div>
 ) : items.map(n => (
 <button
 key={n.id}
 onClick={() => markOneRead(n)}
 className={`w-full text-left px-4 py-3 border-b border-line hover:bg-surface-card flex gap-3 transition-colors ${n.gelesen ? 'opacity-50' : ''}`}
 >
 <span className="text-base flex-shrink-0 mt-0.5">{getTypIcon(n.typ)}</span>
 <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between gap-3">
 <span className="text-xs font-medium text-content">{n.titel}</span>
 <span className="text-[10px] text-[var(--text-tertiary)] flex-shrink-0">{timeAgo(n.created_at, t)}</span>
 </div>
 {n.nachricht && <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">{n.nachricht}</div>}
 <div className="flex items-center gap-2 mt-1">
 <span className="text-[9px] text-[var(--text-tertiary)] bg-surface-card px-1.5 py-0.5 rounded">{n.typ}</span>
 {n.link && <span className="text-[9px] text-primary-500">{n.link}</span>}
 </div>
 </div>
 {!n.gelesen && <span className="w-2 h-2 rounded-full bg-[#338edc] flex-shrink-0 mt-2" />}
 </button>
 ))}
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between mt-4">
 <span className="text-[10px] text-[var(--text-tertiary)]">{t('notifications.pageOf', { page: currentPage, pages: totalPages })}</span>
 <div className="flex gap-1">
 <button
 disabled={offset === 0}
 onClick={() => setOffset(Math.max(0, offset - limit))}
 className="px-3 py-1 text-xs bg-surface-card border border-line-hover rounded-lg text-content-tertiary hover:text-content disabled:opacity-30 disabled:cursor-not-allowed"
 >{t('common.back')}</button>
 <button
 disabled={offset + limit >= total}
 onClick={() => setOffset(offset + limit)}
 className="px-3 py-1 text-xs bg-surface-card border border-line-hover rounded-lg text-content-tertiary hover:text-content disabled:opacity-30 disabled:cursor-not-allowed"
 >{t('common.next')}</button>
 </div>
 </div>
 )}
 </div>
 );
}
